import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  Input,
} from "@chakra-ui/react";
import Uppy, { Meta } from "@uppy/core";
import { DragEventHandler, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit, FaFileUpload } from "react-icons/fa";

export const DropTarget = ({
  targetId,
  uppy,
  helperText,
  label,
  paddingBottom = "8",
  createTextDocument,
  isCreatingTextDocument = false,
}: {
  targetId: string;
  uppy: Uppy<Meta, Record<string, never>>;
  helperText: string;
  label?: string;
  paddingBottom?: string;
  createTextDocument?: () => Promise<unknown> | unknown;
  isCreatingTextDocument?: boolean;
}) => {
  const { t } = useTranslation();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDropping, setIsDropping] = useState(false);

  const onButtonClick = () => {
    fileInputRef.current?.click();
  };

  const onFileChange = (files?: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach((file) =>
      uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
      }),
    );
  };

  const onDragOver: DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    setIsDropping(true);
  };

  const onDrop: DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    onFileChange(event.dataTransfer.files);
    setIsDropping(false);
  };

  return (
    <FormControl pb={paddingBottom}>
      <FormLabel>{label ?? t("Upload documents")}</FormLabel>
      <div id={targetId}>
        <Box
          onDragOver={onDragOver}
          onDragLeave={() => setIsDropping(false)}
          onDrop={onDrop}
          border="2px dashed"
          borderColor={isDropping ? "smSecondary" : "smBorder"}
          rounded="md"
          padding="5"
          textAlign="center"
          cursor="pointer"
          onClick={onButtonClick}
        >
          <Button
            size="sm"
            leftIcon={<Icon as={FaFileUpload} />}
            mr={createTextDocument ? "2" : ""}
          >
            {t("Drop or browse files here")}
          </Button>
          {createTextDocument && (
            <Button
              size="sm"
              leftIcon={<Icon as={FaEdit} />}
              isLoading={isCreatingTextDocument}
              onClick={(e) => {
                e.stopPropagation();
                createTextDocument();
              }}
            >
              {t("Create document")}
            </Button>
          )}
        </Box>
        <Input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          multiple
          onChange={(e) => onFileChange(e.target?.files)}
        />
      </div>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};
